import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { authFetch, clearAuthToken } from '../utils/auth-fetch';

export interface AuthUser {
  id: number;
  email: string;
  name?: string | null;
  playerId?: number | null;
  isAdmin?: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface SessionPayload {
  adminUser?: { id: number; email: string; role: string } | null;
  player?: { id: number; displayName: string } | null;
}

async function fetchSession(): Promise<AuthUser | null> {
  try {
    const res = await authFetch('/api/auth/me');
    if (!res.ok) {
      return null;
    }

    const data = (await res.json()) as SessionPayload;
    if (!data.adminUser) {
      return null;
    }

    return {
      id: data.adminUser.id,
      email: data.adminUser.email,
      name: data.player?.displayName ?? null,
      playerId: data.player?.id ?? null,
      isAdmin: data.adminUser.role !== 'PLAYER',
    };
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await fetchSession();
    setUser(next);
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // swallow — best-effort.
    }

    clearAuthToken();
    setUser(null);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, refresh, logout }),
    [user, loading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return ctx;
}
